<script setup lang="ts">
import { computed, toRefs } from "vue"
import SvgIcon from "@jamescoyle/vue-icon"
import {
  mdiClose,
  mdiFileImageOutline,
  mdiFileVideoOutline,
  mdiFileMusicOutline,
  mdiFilePdfBox,
  mdiFileOutline,
  mdiAlertCircleOutline
} from "@mdi/js"
import { toHumanReadableSize } from "../../size"
import type { BatchEditItem } from "./batchEditItem"
import type { FailedItem } from "./useBatchEditSession"

const props = defineProps<{
  items: BatchEditItem[]
  failed: FailedItem[]
  previewFilename?: (uuid: string) => string | null
}>()

const emit = defineEmits<{ "remove": [uuid: string] }>()

const { items, failed } = toRefs(props)

const totalSize = computed(() => items.value.reduce((a, i) => a + i.length, 0))

const errorMap = computed<Map<string, string>>(() => {
  const m = new Map<string, string>()
  for (const f of failed.value) m.set(f.uuid, f.error.message)
  return m
})

function iconFor(mediaType: string | null | undefined): string {
  const t = mediaType ?? ""
  if (t.startsWith("image/")) return mdiFileImageOutline
  if (t.startsWith("video/")) return mdiFileVideoOutline
  if (t.startsWith("audio/")) return mdiFileMusicOutline
  if (t === "application/pdf") return mdiFilePdfBox
  return mdiFileOutline
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <header
      data-testid="batch-items-header"
      class="px-4 pt-4 pb-2 border-b border-base-300"
    >
      <div class="text-[10px] uppercase tracking-wide text-base-content/60">Auswahl</div>
      <div class="font-semibold text-sm">
        {{ items.length }} Datei{{ items.length === 1 ? '' : 'en' }}
        <span class="text-xs text-base-content/60 ml-1">· {{ toHumanReadableSize(totalSize) }}</span>
      </div>
    </header>
    <ul class="flex-1 overflow-y-auto p-2 space-y-1">
      <li
        v-for="i of items"
        :key="i.uuid"
        data-testid="batch-items-row"
        class="flex items-center gap-2 p-2 rounded-md bg-base-100 border"
        :class="errorMap.has(i.uuid) ? 'border-error' : 'border-base-300'"
        :title="errorMap.get(i.uuid) ?? ''"
      >
        <div class="w-9 h-9 shrink-0 border border-base-300 bg-base-200 rounded-md flex items-center justify-center text-base-content/50">
          <img
            v-if="i.previewUrl"
            :src="i.previewUrl"
            :alt="i.filename"
            class="w-full h-full object-cover rounded-md"
          >
          <svg-icon v-else type="mdi" :path="iconFor(i.mediaType)" class="w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="truncate text-xs font-medium">{{ i.filename }}</div>
          <div
            v-if="previewFilename?.(i.uuid)"
            data-testid="batch-items-preview"
            class="truncate text-xs text-primary"
          >→ {{ previewFilename(i.uuid) }}</div>
          <div class="text-[10px] text-base-content/60">{{ toHumanReadableSize(i.length) }}</div>
        </div>
        <svg-icon
          v-if="errorMap.has(i.uuid)"
          type="mdi"
          :path="mdiAlertCircleOutline"
          class="w-4 h-4 text-error shrink-0"
        />
        <button
          data-testid="batch-items-remove"
          type="button"
          class="btn btn-ghost btn-xs"
          @click="emit('remove', i.uuid)"
        >
          <svg-icon type="mdi" :path="mdiClose" class="w-3 h-3" />
        </button>
      </li>
    </ul>
  </div>
</template>
